import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '../api/client.js';
import { getAiActions } from '../lib/aiActions.js';

/**
 * Markaii AI assistant — a floating ✨ button on every authenticated page that
 * opens a right-hand slide-over chat (ported from the CliLancers assistant,
 * 2026-08-26). The server holds the system prompt; each turn carries a compact
 * snapshot of the open page (when a provider is registered) so the assistant
 * can answer about what's on screen and NAVIGATE the user anywhere in the app.
 *
 * SECURITY: the assistant only reads the page-context snapshot and may emit a
 * client-side navigation op — it has no server-side write path (see
 * lib/aiActions.js). The conversation persists per-browser in localStorage.
 */
const STORE_KEY = 'markaii_ai_chat_v1';
const MAX_STORED = 60;

// In-app deep links in the assistant's replies become real <Link>s.
const APP_PATH_RE = /(\/(?:dashboard|projects|posts|inbox|account|admin)(?:\/[A-Za-z0-9_%-]+)*(?:\?[A-Za-z0-9_=&%-]*)?)/g;

function MessageText({ text }) {
  const parts = String(text || '').split(APP_PATH_RE);
  return parts.map((part, i) => (
    APP_PATH_RE.test(part) && /^\//.test(part)
      ? <Link key={i} to={part} className="ai-chat-link">{part}</Link>
      : <span key={i}>{part}</span>
  ));
}

function loadStored() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.slice(-MAX_STORED) : [];
  } catch { return []; }
}

export default function AiChatDrawer() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(loadStored); // {role, text}
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  const statusQ = useQuery({
    queryKey: ['ai', 'status'],
    queryFn: () => apiGet('/ai/status'),
    staleTime: 10 * 60_000,
    retry: 2,
    refetchInterval: (query) => (query.state.status === 'error' ? 30_000 : false),
  });

  useEffect(() => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(messages.slice(-MAX_STORED))); } catch { /* storage unavailable (private mode / quota) */ }
  }, [messages]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!statusQ.data?.enabled) return null;

  async function send() {
    const text = input.trim();
    if (!text || sending) return;
    setError(null);
    const prev = messages;
    const next = [...messages, { role: 'user', text }];
    const windowMsgs = next.slice(-23); // server rejects > 24 entries
    setMessages(next);
    setInput('');
    setSending(true);
    const provider = getAiActions();
    try {
      const doSend = () => apiPost('/ai/chat', {
        messages: windowMsgs,
        context: provider ? { page: provider.page, data: provider.getContext() } : { page: 'app', data: {} },
      });
      let body;
      try {
        body = await doSend();
      } catch (err) {
        // Absorb a brief deploy-swap 502/503 window with one quiet retry.
        const transient = err.status === 0 || err.status === 502 || err.status === 503 || err.status === 504;
        if (!transient) throw err;
        await new Promise((r) => setTimeout(r, 2500));
        body = await doSend();
      }
      const payload = body.payload || { type: 'message', text: '' };
      if (payload.type === 'ops') {
        const allOps = Array.isArray(payload.ops) ? payload.ops : [];
        const openOps = allOps.filter((o) => o && o.op === 'open'
          && typeof o.path === 'string' && /^\//.test(o.path));
        const editOps = allOps.filter((o) => !openOps.includes(o));
        const applied = provider && provider.apply && editOps.length ? provider.apply(editOps) : [];
        if (openOps.length) {
          navigate(openOps[0].path);
          applied.push(`Opened ${openOps[0].path}`);
        }
        const confirmation = applied.length
          ? `${payload.summary || 'Done.'}\n\n✅ ${applied.join('\n✅ ')}`
          : (payload.summary || 'Done.');
        setMessages((m) => [...m, { role: 'model', text: confirmation }]);
      } else {
        setMessages((m) => [...m, { role: 'model', text: payload.text || '(empty reply)' }]);
      }
    } catch (err) {
      setError(err.body?.error || err.message || 'Something went wrong.');
      setInput(text);       // put the unsent text back
      setMessages(prev);
    } finally {
      setSending(false);
    }
  }

  const provider = getAiActions();
  const canSeePage = Boolean(provider);

  return (
    <>
      {!open && (
        <button
          type="button"
          className="ai-chat-fab"
          title="Markaii assistant"
          aria-label="Open Markaii assistant"
          onClick={() => setOpen(true)}
        >
          ✨
        </button>
      )}
      {open && (
        <div className="ai-chat-drawer" role="dialog" aria-label="Markaii assistant">
          <div className="ai-chat-head">
            <span className="ai-chat-title">✨ Markaii assistant</span>
            {messages.length > 0 && (
              <button type="button" className="btn-icon" title="Clear conversation" aria-label="Clear conversation"
                onClick={() => setMessages([])}>🗑</button>
            )}
            <button type="button" className="btn-icon" aria-label="Close assistant" onClick={() => setOpen(false)}>×</button>
          </div>

          <div className="ai-chat-list" ref={listRef}>
            {messages.length === 0 && (
              <div className="ai-chat-empty">
                Ask me to take you anywhere in Markaii, or about what's on this page —
                “open the inbox”, “show CliLancers' posts”, “which posts are awaiting review?”.
                {canSeePage && (
                  <>
                    <br /><br />
                    👁 I can see this page's data — ask me about what's on screen.
                  </>
                )}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`ai-chat-msg ai-chat-${m.role}`}>
                {m.role === 'model' ? <MessageText text={m.text} /> : m.text}
              </div>
            ))}
            {sending && <div className="ai-chat-msg ai-chat-model ai-chat-pending">…</div>}
            {error && <div className="ai-chat-error">{error}</div>}
          </div>

          <div className="ai-chat-inputrow">
            <textarea
              className="ai-chat-input"
              rows={2}
              value={input}
              placeholder="Ask the assistant…"
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              disabled={sending}
            />
            <button
              type="button"
              className="btn btn-primary ai-chat-send"
              disabled={sending || !input.trim()}
              onClick={send}
              aria-label="Send"
            >
              {sending ? '…' : '➤'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
